import { GenericCrudPage, statusColumn, type CrudConfig } from "./components/GenericCrudPage";
import { SettingsPage } from "./components/SettingsPage";
import type { FieldDef } from "./components/admin-ui";
import { programs, type Program } from "@/mocks/programs";
import { scheduleToday, type ScheduleItem } from "@/mocks/schedule";
import { videos, type VideoItem } from "@/mocks/videos";
import {
  directSeeds,
  sportsSeeds,
  entertainmentSeeds,
  religionSeeds,
  photoStudioSeeds,
  productionSeeds,
  gallerySeeds,
  pageSeeds,
  jobSeeds,
  adSeeds,
  partnerSeeds,
  subscriberSeeds,
  socialSettingsSeed,
  siteSettingsSeed,
  seoSettingsSeed,
  advertSeeds,
  type Advert,
} from "@/mocks/cms";

const programCategories = [
  "News", "Entertainment", "Culture", "Sports", "Religion", "Children",
  "Education", "Talk Shows", "Music", "Community", "Documentary",
];

const videoCategories = [
  "News", "Interviews", "Reports", "Talk Shows", "Entertainment", "Culture",
  "Religion", "Children", "Sports", "Documentaries", "L+TV Direct",
  "Behind the Scenes", "Short Videos",
];

const statusField = (options: { value: string; label: string }[]): FieldDef => ({
  name: "status",
  label: "Status",
  type: "select",
  options,
});

const toggleFeatured: FieldDef = { name: "featured", label: "Featured", type: "toggle" };

// -------- Programs --------
const programsConfig: CrudConfig<Program> = {
  title: "Programs",
  description: "Manage the L+TV program directory.",
  storageKey: "ltv_programs_v1",
  seed: programs,
  newLabel: "Program",
  searchKeys: ["name", "presenter", "category"],
  fields: [
    { name: "name", label: "Program name", type: "text", required: true, full: true },
    { name: "description", label: "Description", type: "textarea", full: true },
    { name: "presenter", label: "Presenter", type: "text" },
    { name: "category", label: "Category", type: "select", options: programCategories.map((c) => ({ value: c, label: c })) },
    { name: "department", label: "Department", type: "text" },
    { name: "broadcastDays", label: "Broadcast days", type: "tags" },
    { name: "broadcastTime", label: "Broadcast time", type: "text", placeholder: "18:00" },
    { name: "duration", label: "Duration", type: "text", placeholder: "60 min" },
    toggleFeatured,
  ],
  columns: [
    { key: "name", label: "Program" },
    { key: "category", label: "Category" },
    { key: "presenter", label: "Presenter" },
    { key: "broadcastTime", label: "Time" },
  ],
};

// -------- TV Schedule --------
const scheduleConfig: CrudConfig<ScheduleItem> = {
  title: "TV Schedule",
  description: "Manage the electronic program guide.",
  storageKey: "ltv_schedule_v1",
  seed: scheduleToday,
  newLabel: "Schedule Item",
  searchKeys: ["program", "category"],
  fields: [
    { name: "time", label: "Time", type: "text", placeholder: "18:00", required: true },
    { name: "program", label: "Program", type: "text", required: true },
    { name: "category", label: "Category", type: "text" },
    { name: "duration", label: "Duration", type: "text", placeholder: "60 min" },
    { name: "description", label: "Description", type: "textarea", full: true },
    { name: "live", label: "Live broadcast", type: "toggle" },
  ],
  columns: [
    { key: "time", label: "Time" },
    { key: "program", label: "Program" },
    { key: "category", label: "Category" },
    { key: "live", label: "Live", render: (it) => (it.live ? "● LIVE" : "—") },
  ],
};

// -------- L+TV Direct --------
const directConfig: CrudConfig<typeof directSeeds[number]> = {
  title: "L+TV Direct",
  description: "Manage live sessions, interviews and interactive broadcasts.",
  storageKey: "ltv_direct_v1",
  seed: directSeeds,
  newLabel: "Direct Session",
  searchKeys: ["title", "presenter", "category"],
  filter: { key: "status", label: "Status", options: [{ value: "draft", label: "Draft" }, { value: "scheduled", label: "Scheduled" }, { value: "live", label: "Live" }, { value: "completed", label: "Completed" }, { value: "cancelled", label: "Cancelled" }] },
  fields: [
    { name: "title", label: "Session title", type: "text", required: true, full: true },
    { name: "description", label: "Description", type: "textarea", full: true },
    { name: "presenter", label: "Presenter", type: "text" },
    { name: "category", label: "Category", type: "text" },
    { name: "date", label: "Date", type: "date" },
    { name: "startTime", label: "Start time", type: "time" },
    { name: "endTime", label: "End time", type: "time" },
    { name: "source", label: "Stream source", type: "select", options: [{ value: "Website Direct Stream", label: "Website Direct Stream" }, { value: "YouTube Live", label: "YouTube Live" }, { value: "Facebook Live", label: "Facebook Live" }, { value: "TikTok Live", label: "TikTok Live" }, { value: "External Stream", label: "External Stream" }] },
    statusField([{ value: "draft", label: "Draft" }, { value: "scheduled", label: "Scheduled" }, { value: "live", label: "Live" }, { value: "completed", label: "Completed" }, { value: "cancelled", label: "Cancelled" }]),
  ],
  columns: [
    { key: "title", label: "Session" },
    { key: "category", label: "Category" },
    { key: "source", label: "Source" },
    { key: "status", label: "Status", render: (it) => statusColumn(it.status) },
  ],
};

// -------- Section posts (Sports / Entertainment / Religion) --------
function sectionConfig(opts: {
  title: string;
  description: string;
  storageKey: string;
  seed: typeof sportsSeeds;
}): CrudConfig<typeof sportsSeeds[number]> {
  return {
    title: opts.title,
    description: opts.description,
    storageKey: opts.storageKey,
    seed: opts.seed,
    newLabel: "Post",
    searchKeys: ["title", "category", "excerpt"],
    fields: [
      { name: "title", label: "Title", type: "text", required: true, full: true },
      { name: "category", label: "Category", type: "text" },
      { name: "excerpt", label: "Excerpt", type: "textarea", full: true },
      { name: "content", label: "Content", type: "textarea", full: true },
      { name: "image", label: "Image URL", type: "text", full: true },
      { name: "date", label: "Date", type: "date" },
      toggleFeatured,
      statusField([{ value: "draft", label: "Draft" }, { value: "published", label: "Published" }]),
    ],
    columns: [
      { key: "title", label: "Title" },
      { key: "category", label: "Category" },
      { key: "status", label: "Status", render: (it) => statusColumn(it.status) },
    ],
  };
}

// -------- Services (Photo Studio / Production) --------
function serviceConfig(opts: {
  title: string;
  description: string;
  storageKey: string;
  seed: typeof photoStudioSeeds;
}): CrudConfig<typeof photoStudioSeeds[number]> {
  return {
    title: opts.title,
    description: opts.description,
    storageKey: opts.storageKey,
    seed: opts.seed,
    newLabel: "Service",
    searchKeys: ["name", "category"],
    fields: [
      { name: "name", label: "Service name", type: "text", required: true, full: true },
      { name: "description", label: "Description", type: "textarea", full: true },
      { name: "category", label: "Category", type: "text" },
      { name: "image", label: "Image URL", type: "text", full: true },
      statusField([{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }]),
    ],
    columns: [
      { key: "name", label: "Service" },
      { key: "category", label: "Category" },
      { key: "status", label: "Status", render: (it) => statusColumn(it.status) },
    ],
  };
}

// -------- Gallery --------
const galleryConfig: CrudConfig<typeof gallerySeeds[number]> = {
  title: "Gallery",
  description: "Manage photo albums and galleries.",
  storageKey: "ltv_gallery_v1",
  seed: gallerySeeds,
  newLabel: "Album",
  searchKeys: ["name", "category"],
  fields: [
    { name: "name", label: "Album name", type: "text", required: true, full: true },
    { name: "category", label: "Category", type: "text" },
    { name: "description", label: "Description", type: "textarea", full: true },
    { name: "cover", label: "Cover image URL", type: "text", full: true },
    { name: "count", label: "Photo count", type: "text" },
    statusField([{ value: "published", label: "Published" }, { value: "unpublished", label: "Unpublished" }]),
  ],
  columns: [
    { key: "name", label: "Album" },
    { key: "category", label: "Category" },
    { key: "status", label: "Status", render: (it) => statusColumn(it.status) },
  ],
};

// -------- Pages --------
const pagesConfig: CrudConfig<typeof pageSeeds[number]> = {
  title: "Pages",
  description: "Manage static website pages.",
  storageKey: "ltv_pages_v1",
  seed: pageSeeds,
  newLabel: "Page",
  searchKeys: ["title", "slug"],
  fields: [
    { name: "title", label: "Title", type: "text", required: true, full: true },
    { name: "slug", label: "URL slug", type: "text" },
    { name: "content", label: "Content", type: "textarea", full: true },
    statusField([{ value: "published", label: "Published" }, { value: "draft", label: "Draft" }]),
  ],
  columns: [
    { key: "title", label: "Page" },
    { key: "slug", label: "Slug" },
    { key: "status", label: "Status", render: (it) => statusColumn(it.status) },
  ],
};

// -------- Careers --------
const jobsConfig: CrudConfig<typeof jobSeeds[number]> = {
  title: "Careers",
  description: "Manage job vacancies and applications.",
  storageKey: "ltv_jobs_v1",
  seed: jobSeeds,
  newLabel: "Job",
  searchKeys: ["title", "department", "location"],
  filter: { key: "status", label: "Status", options: [{ value: "open", label: "Open" }, { value: "closed", label: "Closed" }] },
  fields: [
    { name: "title", label: "Job title", type: "text", required: true, full: true },
    { name: "department", label: "Department", type: "text" },
    { name: "location", label: "Location", type: "text" },
    { name: "employmentType", label: "Employment type", type: "text" },
    { name: "deadline", label: "Deadline", type: "date" },
    { name: "description", label: "Description", type: "textarea", full: true },
    { name: "requirements", label: "Requirements", type: "textarea", full: true },
    { name: "responsibilities", label: "Responsibilities", type: "textarea", full: true },
    statusField([{ value: "open", label: "Open" }, { value: "closed", label: "Closed" }]),
  ],
  columns: [
    { key: "title", label: "Job" },
    { key: "department", label: "Department" },
    { key: "location", label: "Location" },
    { key: "status", label: "Status", render: (it) => statusColumn(it.status) },
  ],
};

// -------- Advertising --------
const adsConfig: CrudConfig<typeof adSeeds[number]> = {
  title: "Advertising",
  description: "Manage advertising packages, campaigns and enquiries.",
  storageKey: "ltv_ads_v1",
  seed: adSeeds,
  newLabel: "Campaign",
  searchKeys: ["title", "placement"],
  fields: [
    { name: "title", label: "Campaign title", type: "text", required: true, full: true },
    { name: "placement", label: "Placement", type: "text" },
    { name: "startDate", label: "Start date", type: "date" },
    { name: "endDate", label: "End date", type: "date" },
    { name: "link", label: "Link", type: "text" },
    { name: "description", label: "Description", type: "textarea", full: true },
    statusField([{ value: "active", label: "Active" }, { value: "paused", label: "Paused" }, { value: "scheduled", label: "Scheduled" }, { value: "expired", label: "Expired" }]),
  ],
  columns: [
    { key: "title", label: "Campaign" },
    { key: "placement", label: "Placement" },
    { key: "status", label: "Status", render: (it) => statusColumn(it.status) },
  ],
};

// -------- Adverts --------
const advertsConfig: CrudConfig<Advert> = {
  title: "Adverts",
  description: "Publish and manage the adverts & announcements shown on the homepage.",
  storageKey: "ltv_adverts_v1",
  seed: advertSeeds,
  newLabel: "Advert",
  searchKeys: ["title", "tagline", "placement"],
  filter: { key: "status", label: "Status", options: [{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }] },
  fields: [
    { name: "title", label: "Advert title", type: "text", required: true, full: true },
    { name: "tagline", label: "Tagline", type: "text", full: true },
    { name: "image", label: "Image URL", type: "text", full: true, help: "Paste the flyer or visual URL shown with this advert." },
    { name: "description", label: "Description", type: "textarea", full: true },
    { name: "link", label: "Link (optional)", type: "text", full: true, help: "Where the 'Learn more' button points, e.g. /news" },
    { name: "placement", label: "Placement", type: "text" },
    { name: "startDate", label: "Start date", type: "date" },
    { name: "endDate", label: "End date", type: "date" },
    { name: "marquee", label: "Show title in the banner ticker", type: "toggle" },
    statusField([{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }]),
  ],
  columns: [
    { key: "title", label: "Advert", render: (it) => (
      <span className="inline-flex items-center gap-3">
        {it.image ? (
          <img src={it.image} alt={it.title} className="w-12 h-9 object-cover rounded border border-background-200" />
        ) : null}
        <span className="font-medium text-foreground-950">{it.title}</span>
      </span>
    ) },
    { key: "placement", label: "Placement" },
    { key: "status", label: "Status", render: (it) => statusColumn(it.status) },
  ],
};

// -------- Partners --------
const partnersConfig: CrudConfig<typeof partnerSeeds[number]> = {
  title: "Partners",
  description: "Manage partner and collaboration listings.",
  storageKey: "ltv_partners_v1",
  seed: partnerSeeds,
  newLabel: "Partner",
  searchKeys: ["name"],
  fields: [
    { name: "name", label: "Partner name", type: "text", required: true, full: true },
    { name: "description", label: "Description", type: "textarea", full: true },
    { name: "website", label: "Website", type: "text" },
    statusField([{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }]),
  ],
  columns: [
    { key: "name", label: "Partner" },
    { key: "status", label: "Status", render: (it) => statusColumn(it.status) },
  ],
};

// -------- Newsletter --------
const newsletterConfig: CrudConfig<typeof subscriberSeeds[number]> = {
  title: "Newsletter",
  description: "Manage newsletter subscribers.",
  storageKey: "ltv_newsletter_v1",
  seed: subscriberSeeds,
  newLabel: "Subscriber",
  searchKeys: ["name", "email"],
  fields: [
    { name: "name", label: "Name", type: "text", required: true },
    { name: "email", label: "Email", type: "text", required: true },
    { name: "date", label: "Date", type: "date" },
    statusField([{ value: "subscribed", label: "Subscribed" }, { value: "unsubscribed", label: "Unsubscribed" }]),
  ],
  columns: [
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "status", label: "Status", render: (it) => statusColumn(it.status) },
  ],
};

// -------- Videos --------
const videosConfig: CrudConfig<VideoItem> = {
  title: "Videos",
  description: "Manage videos and on-demand content.",
  storageKey: "ltv_videos_v1",
  seed: videos,
  newLabel: "Video",
  searchKeys: ["title", "category", "tags"],
  fields: [
    { name: "title", label: "Title", type: "text", required: true, full: true },
    { name: "description", label: "Description", type: "textarea", full: true },
    { name: "category", label: "Category", type: "select", options: videoCategories.map((c) => ({ value: c, label: c })) },
    { name: "thumbnail", label: "Thumbnail URL", type: "text", full: true },
    { name: "videoUrl", label: "Video URL (MP4 / stream)", type: "text", full: true, help: "Enter a hosted video URL. Real file upload requires connected storage." },
    { name: "tags", label: "Tags", type: "tags" },
    { name: "duration", label: "Duration", type: "text" },
    { name: "views", label: "Views", type: "text" },
    toggleFeatured,
  ],
  columns: [
    { key: "title", label: "Title" },
    { key: "category", label: "Category" },
    { key: "duration", label: "Duration" },
  ],
};

// -------- Settings (single record) --------
const socialFields: FieldDef[] = [
  { name: "facebook", label: "Facebook URL", type: "text" },
  { name: "youtube", label: "YouTube URL", type: "text" },
  { name: "tiktok", label: "TikTok URL", type: "text" },
  { name: "instagram", label: "Instagram URL", type: "text" },
  { name: "twitter", label: "X / Twitter URL", type: "text" },
  { name: "whatsapp", label: "WhatsApp", type: "text" },
];

const siteFields: FieldDef[] = [
  { name: "siteName", label: "Website name", type: "text", required: true },
  { name: "tagline", label: "Tagline", type: "text", full: true },
  { name: "email", label: "Email", type: "text" },
  { name: "phone", label: "Phone", type: "text" },
  { name: "address", label: "Address", type: "text", full: true },
  { name: "poBox", label: "P.O. Box", type: "text" },
  { name: "copyright", label: "Copyright", type: "text", full: true },
];

const seoFields: FieldDef[] = [
  { name: "siteTitle", label: "Site title", type: "text", full: true },
  { name: "metaDescription", label: "Meta description", type: "textarea", full: true },
  { name: "keywords", label: "Keywords", type: "text", full: true },
  { name: "socialImage", label: "Social image URL", type: "text", full: true },
  { name: "websiteUrl", label: "Website URL", type: "text" },
];

// -------- Exported pages --------
export function ProgramsAdminPage() { return <GenericCrudPage config={programsConfig} />; }
export function ScheduleAdminPage() { return <GenericCrudPage config={scheduleConfig} />; }
export function DirectAdminPage() { return <GenericCrudPage config={directConfig} />; }
export function SportsAdminPage() { return <GenericCrudPage config={sectionConfig({ title: "Sports", description: "Manage sports news, match reports, interviews and results.", storageKey: "ltv_sports_v1", seed: sportsSeeds })} />; }
export function EntertainmentAdminPage() { return <GenericCrudPage config={sectionConfig({ title: "Entertainment & Culture", description: "Manage entertainment, music, youth and culture content.", storageKey: "ltv_entertainment_v1", seed: entertainmentSeeds })} />; }
export function ReligionAdminPage() { return <GenericCrudPage config={sectionConfig({ title: "Religion & Community", description: "Manage biblical teaching, religious discussions and community content.", storageKey: "ltv_religion_v1", seed: religionSeeds })} />; }
export function PhotoStudioAdminPage() { return <GenericCrudPage config={serviceConfig({ title: "Photo Studio", description: "Manage photo studio services and portfolio.", storageKey: "ltv_photo_v1", seed: photoStudioSeeds })} />; }
export function ProductionAdminPage() { return <GenericCrudPage config={serviceConfig({ title: "Production", description: "Manage production services and projects.", storageKey: "ltv_production_v1", seed: productionSeeds })} />; }
export function GalleryAdminPage() { return <GenericCrudPage config={galleryConfig} />; }
export function PagesAdminPage() { return <GenericCrudPage config={pagesConfig} />; }
export function CareersAdminPage() { return <GenericCrudPage config={jobsConfig} />; }
export function AdvertisingAdminPage() { return <GenericCrudPage config={adsConfig} />; }
export function PartnersAdminPage() { return <GenericCrudPage config={partnersConfig} />; }
export function NewsletterAdminPage() { return <GenericCrudPage config={newsletterConfig} />; }
export function VideosAdminPage() { return <GenericCrudPage config={videosConfig} />; }
export function AdvertsAdminPage() { return <GenericCrudPage config={advertsConfig} />; }

export function SocialAdminPage() {
  return (
    <SettingsPage
      title="Social Media"
      description="Manage social media links used across the website."
      storageKey="ltv_social_v1"
      seed={socialSettingsSeed}
      fields={socialFields}
    />
  );
}

export function WebsiteSettingsAdminPage() {
  return (
    <SettingsPage
      title="Website Settings"
      description="Manage general website branding and contact information."
      storageKey="ltv_site_settings_v1"
      seed={siteSettingsSeed}
      fields={siteFields}
    />
  );
}

export function SeoAdminPage() {
  return (
    <SettingsPage
      title="SEO"
      description="Manage global search engine optimization settings."
      storageKey="ltv_seo_v1"
      seed={seoSettingsSeed}
      fields={seoFields}
    />
  );
}